import { useAuth } from 'contexts/auth';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

const defaultForm = {
    id: uuid(),
    courseName: '',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { changeAccount, confirmCourseSetUp } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation('course');

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        description: '',
        variant: 'primary',
        actionButtonText: '',
        onAction: () => {},
    });
    const [errors, setErrors] = useState({
        courseName: '',
    });

    const handleChange = async (key, data) => {
        switch (key) {
            case 'courseName': {
                return setForm((prev) => ({ ...prev, courseName: data }));
            }
            default:
                return;
        }
    };

    const getErrorMessage = (key, data) => {
        switch (key) {
            case 'courseName': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0
                    ? ''
                    : t('course-setup.blank-error-message', 'Course name cannot be left blank');
            }
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors)
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    const handleClose = () => {
        changeAccount();
    };

    const submitData = async () => {
        if (!validateAll()) return;
        setLoading(true);
        const data = {
            id: form.id,
            name: form.courseName,
        };
        await confirmCourseSetUp(data);
        return history.push('/');
    };

    useEffect(() => {
        if (location.state?.duplicatingCourse) {
            setForm((current) => ({ ...current, courseName: location.state.duplicatingCourse.name }));
        }
    }, [location.state]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                setForm,
                errors,
                setErrors,
                dialog,
                setDialog,
                handleChange,
                handleClose,
                submitData,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
